/**
 * A new order. The customer is picked from the business's list or named on the spot, because the
 * first order is usually when a customer is met. Only the description is required besides; items,
 * a quote and a due date can follow.
 */

import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice, Select } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { businessesQuery } from "@/features/settings/queries";
import { fieldMessage } from "@/lib/fieldMessage";
import { customersQuery, useCreateCustomer, useCreateOrder } from "./queries";
import { emptyNewOrder, newOrderSchema, toOrderBody, type NewOrderValues } from "./schemas";

export function NewOrderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const currency = me.data?.workspace?.default_currency ?? "CAD";
  const businesses = useQuery(businessesQuery);
  const customers = useQuery(customersQuery);
  const createCustomer = useCreateCustomer();
  const createOrder = useCreateOrder();

  const form = useForm<NewOrderValues>({
    resolver: zodResolver(newOrderSchema),
    defaultValues: emptyNewOrder,
  });
  const items = useFieldArray({ control: form.control, name: "items" });
  const errors = form.formState.errors;
  const active = (businesses.data ?? []).filter((business) => business.is_active);
  const businessId = useWatch({ control: form.control, name: "business_id" });
  const customerId = useWatch({ control: form.control, name: "customer_id" });
  const theirs = (customers.data ?? []).filter((customer) => customer.business_id === businessId);

  // One business is the common case: choose it rather than make the user.
  useEffect(() => {
    const open = (businesses.data ?? []).filter((business) => business.is_active);
    const only = open.length === 1 ? open[0] : undefined;
    if (only && !form.getValues("business_id")) form.setValue("business_id", only.id);
  }, [businesses.data, form]);

  const submit = form.handleSubmit(async (values) => {
    try {
      const customer =
        values.customer_id ||
        (
          await createCustomer.mutateAsync({
            business_id: values.business_id,
            name: values.new_customer,
            email: "",
            phone: "",
            notes: "",
          })
        ).id;
      const order = await createOrder.mutateAsync(toOrderBody(values, customer, currency));
      await navigate({ to: "/orders/$orderId", params: { orderId: order.id } });
    } catch (error) {
      form.setError("root", {
        type: "server",
        message: error instanceof ApiError ? error.message : t("orders.errors.saveFailed"),
      });
    }
  });

  function itemError(index: number, field: "description" | "quantity" | "price") {
    const message = errors.items?.[index]?.[field]?.message;
    return message ? t(message) : undefined;
  }

  return (
    <section className="space-y-5">
      <Link
        to="/orders"
        className="-ml-1 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t("orders.title")}
      </Link>
      <h1 className="text-xl font-semibold">{t("orders.new.title")}</h1>

      {businesses.isSuccess && active.length === 0 ? (
        <Notice tone="info">{t("orders.new.needsBusiness")}</Notice>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          {active.length > 1 && (
            <Field
              name="business_id"
              label={t("orders.new.business")}
              error={fieldMessage(errors, "business_id", t)}
            >
              <Select id="business_id" {...form.register("business_id")}>
                <option value="">{t("orders.new.chooseBusiness")}</option>
                {active.map((business) => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field name="customer_id" label={t("orders.new.customer")}>
            <Select id="customer_id" disabled={!businessId} {...form.register("customer_id")}>
              <option value="">{t("orders.new.newCustomer")}</option>
              {theirs.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </Select>
          </Field>
          {!customerId && (
            <Field
              name="new_customer"
              label={t("orders.new.customerName")}
              error={fieldMessage(errors, "new_customer", t)}
            >
              <Input id="new_customer" autoComplete="off" {...form.register("new_customer")} />
            </Field>
          )}

          <Field
            name="description"
            label={t("orders.new.description")}
            error={fieldMessage(errors, "description", t)}
          >
            <Input
              id="description"
              placeholder={t("orders.new.descriptionPlaceholder")}
              {...form.register("description")}
            />
          </Field>
          <div className="flex gap-3">
            <div className="flex-1">
              <Field name="due_date" label={t("orders.new.dueDate")}>
                <Input id="due_date" type="date" {...form.register("due_date")} />
              </Field>
            </div>
            <div className="flex-1">
              <Field
                name="quote"
                label={t("orders.new.quote", { currency })}
                error={fieldMessage(errors, "quote", t)}
              >
                <Input id="quote" inputMode="decimal" {...form.register("quote")} />
              </Field>
            </div>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-stone-700">{t("orders.new.items")}</legend>
            {items.fields.map((item, index) => (
              <div
                key={item.id}
                className="space-y-2 rounded-lg border border-stone-200 bg-white p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Field
                      name={`item-description-${index}`}
                      label={t("orders.new.itemDescription")}
                      error={itemError(index, "description")}
                    >
                      <Input
                        id={`item-description-${index}`}
                        {...form.register(`items.${index}.description`)}
                      />
                    </Field>
                  </div>
                  <button
                    type="button"
                    className="mt-7 p-2 text-stone-500"
                    aria-label={t("orders.new.removeItem")}
                    onClick={() => items.remove(index)}
                  >
                    <X className="h-5 w-5" aria-hidden />
                  </button>
                </div>
                <div className="flex gap-2">
                  <div className="w-24">
                    <Field
                      name={`item-quantity-${index}`}
                      label={t("orders.new.quantity")}
                      error={itemError(index, "quantity")}
                    >
                      <Input
                        id={`item-quantity-${index}`}
                        inputMode="decimal"
                        {...form.register(`items.${index}.quantity`)}
                      />
                    </Field>
                  </div>
                  <div className="flex-1">
                    <Field
                      name={`item-price-${index}`}
                      label={t("orders.new.unitPrice")}
                      error={itemError(index, "price")}
                    >
                      <Input
                        id={`item-price-${index}`}
                        inputMode="decimal"
                        {...form.register(`items.${index}.price`)}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="text-brand-700 inline-flex items-center gap-1 text-sm underline"
              onClick={() => items.append({ description: "", quantity: "1", price: "" })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t("orders.new.addItem")}
            </button>
          </fieldset>

          <Field name="notes" label={t("orders.new.notes")}>
            <Input id="notes" {...form.register("notes")} />
          </Field>

          {errors.root?.message && <Notice tone="error">{errors.root.message}</Notice>}
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? t("common.saving") : t("orders.new.save")}
          </Button>
        </form>
      )}
    </section>
  );
}
